// Copyright © 2026 Atomic.io Limited. All rights reserved.

const LITERAL_VALUES = {
  NaN: Number.NaN,
  Infinity: Number.POSITIVE_INFINITY,
  '-Infinity': Number.NEGATIVE_INFINITY,
  null: null,
  undefined: undefined,
  true: true,
  false: false,
}

const QUOTED_STRING = /^(['"])(.*)\1$/s

/**
 * Converts the text entered for a flush interval into the value passed to `AtomicSDK.setEventFlushInterval`, so that
 * testers can supply non-number values as well as numbers.
 *
 * - `NaN`, `Infinity`, `-Infinity`, `null`, `undefined`, `true` and `false` become those values.
 * - Text wrapped in single or double quotes becomes a string, e.g. `'15'` is the string "15".
 * - Any other text that is a valid number becomes that number, e.g. `15` or `2.5`.
 * - Anything else is passed as a string.
 * @param {string} text The text entered by the tester.
 * @returns {{value: unknown, description: string}} The value to pass, and how it appears as a JavaScript literal.
 */
export const parseFlushIntervalInput = text => {
  const trimmed = text.trim()

  if (Object.prototype.hasOwnProperty.call(LITERAL_VALUES, trimmed)) {
    return {value: LITERAL_VALUES[trimmed], description: trimmed}
  }

  const quoted = trimmed.match(QUOTED_STRING)
  if (quoted) {
    return {value: quoted[2], description: JSON.stringify(quoted[2])}
  }

  if (trimmed.length > 0 && !Number.isNaN(Number(trimmed))) {
    const value = Number(trimmed)
    return {value, description: String(value)}
  }

  return {value: trimmed, description: JSON.stringify(trimmed)}
}
